<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
      <header class="mb-8">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
              Meu
              <span class="bg-clip-text text-transparent bg-gradient-to-r from-brand-500 to-brand-400">Perfil</span>
            </h1>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Consulte suas informações pessoais e de acesso.
            </p>
          </div>
        </div>
      </header>

      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <span class="material-symbols-outlined animate-spin text-4xl text-brand-500">progress_activity</span>
        <p class="mt-4 text-slate-600 dark:text-slate-400">Carregando seu perfil...</p>
      </div>

      <div v-if="message.text && !loading" :class="[
        'flex items-center text-sm p-3 rounded-lg mb-6 font-medium',
        message.type === 'success'
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-700/20 dark:text-emerald-300'
          : 'bg-red-50 text-red-700 dark:bg-red-700/20 dark:text-red-300'
      ]">
        <span class="material-symbols-outlined mr-2 text-lg">
          {{ message.type === 'success' ? 'check_circle' : 'error' }}
        </span>
        {{ message.text }}
      </div>

      <div v-if="!loading && !message.text" class="space-y-8">
        <div class="flex flex-col items-center justify-center mt-8">
          <div class="user-avatar bg-brand-500/10 dark:bg-brand-700 text-brand-600 dark:text-brand-200"
            style="width: 128px; height: 128px;"> <span class="text-5xl font-bold">{{ userInitials }}</span> </div>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section class="lg:col-span-2 card p-0 overflow-hidden">
            <div class="p-6 sm:p-8">
              <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-5">
                Informações Pessoais
              </h3>
              <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div class="stat-row">
                  <dt>Nome Completo</dt>
                  <dd>{{ usuario.nome || '—' }}</dd>
                </div>
                <div class="stat-row">
                  <dt>Nome de Usuário</dt>
                  <dd>{{ usuario.username || '—' }}</dd>
                </div>
                <div class="stat-row sm:col-span-2">
                  <dt>Email</dt>
                  <dd>{{ usuario.email || '—' }}</dd>
                </div>
              </dl>
            </div>

            <div class="p-6 sm:p-8 border-t form-section-border">
              <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-5">
                Informações de Contato
              </h3>
              <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div class="stat-row">
                  <dt>Telefone</dt>
                  <dd>{{ usuario.telefone || '—' }}</dd>
                </div>
              </dl>
            </div>

            <div class="p-6 sm:p-8 border-t form-section-border">
              <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-5">
                Endereço
              </h3>
              <dl class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div class="stat-row">
                  <dt>CEP</dt>
                  <dd>{{ usuario.endereco.cep || '—' }}</dd>
                </div>
                <div class="stat-row sm:col-span-2">
                  <dt>Logradouro</dt>
                  <dd>{{ usuario.endereco.logradouro || '—' }}</dd>
                </div>
                <div class="stat-row">
                  <dt>Número</dt>
                  <dd>{{ usuario.endereco.numero || '—' }}</dd>
                </div>
                <div class="stat-row sm:col-span-2">
                  <dt>Complemento</dt>
                  <dd>{{ usuario.endereco.complemento || '—' }}</dd>
                </div>
                <div class="stat-row">
                  <dt>Bairro</dt>
                  <dd>{{ usuario.endereco.bairro || '—' }}</dd>
                </div>
                <div class="stat-row">
                  <dt>Cidade</dt>
                  <dd>{{ usuario.endereco.localidade || '—' }}</dd>
                </div>
                <div class="stat-row">
                  <dt>UF</dt>
                  <dd>{{ usuario.endereco.uf || '—' }}</dd>
                </div>
              </dl>
            </div>
          </section>

          <aside class="lg:col-span-1 card p-0 overflow-hidden">
            <div class="p-6 sm:p-7 faq-header-border">
              <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
                Dúvidas rápidas
              </h3>
              <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Informações sobre seus dados.
              </p>
            </div>
            <ul class="divide-y faq-divide-border">
              <li class="faq-row">
                <div>
                  <div class="faq-q">Por que não posso mudar meu e-mail?</div>
                  <div class="faq-a">
                    O e-mail é seu identificador único. Para alterá-lo, por favor, entre em contato com o suporte.
                  </div>
                </div>
              </li>
              <li class="faq-row">
                <div>
                  <div class="faq-q">Preciso alterar meus dados, como faço?</div>
                  <div class="faq-a">
                    No momento, a alteração de dados deve ser solicitada ao nosso suporte.
                  </div>
                </div>
              </li>
              <li class="faq-row">
                <div>
                  <div class="faq-q">Meus dados estão seguros?</div>
                  <div class="faq-a">
                    Sim. Suas informações são armazenadas com segurança e usadas apenas para fins de identificação.
                  </div>
                </div>
              </li>
            </ul>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'

const apiUrl = 'https://backend.controlease.com.br'
const userId = localStorage.getItem('usuario_id')

const usuario = ref({
  id: userId,
  nome: '',
  username: '',
  email: '',
  telefone: '',
  endereco: {
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    localidade: '',
    uf: ''
  }
})

const loading = ref(true)
const message = ref({ text: '', type: '' })

const userInitials = computed(() => {
  if (!usuario.value.nome) return '??'
  const parts = usuario.value.nome.split(' ').filter(Boolean)
  if (parts.length === 0) return '??'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
})

const normalizeTelefone = (val) => {
  const digits = (val || '').replace(/\D/g, '')
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) return digits.slice(2)
  return digits
}

const authConfig = () => {
  const token = localStorage.getItem('token')
  if (token) {
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  }
  message.value = {
    text: 'Erro de autenticação. Faça login novamente.',
    type: 'error'
  }
  return { headers: {} }
}

const fetchUser = async () => {
  if (!userId) {
    message.value = {
      text: 'ID do usuário não encontrado. Faça login novamente.',
      type: 'error'
    }
    loading.value = false
    return
  }

  loading.value = true
  message.value = { text: '', type: '' }
  try {
    const config = authConfig()
    if (!config.headers.Authorization) throw new Error('Token ausente')

    const { data } = await axios.get(
      `${apiUrl}/api/users/${userId}`,
      config
    )

    const incomingPhone = data.telefone ?? data.phone ?? ''
    const formattedTelefone = formatarTelefoneExibicao(
      normalizeTelefone(incomingPhone)
    )

    usuario.value.id = data.id || userId
    usuario.value.nome = data.nome || ''
    usuario.value.username = data.username || ''
    usuario.value.email = data.email || ''
    usuario.value.telefone = formattedTelefone

    usuario.value.endereco.cep = data.endereco?.cep || ''
    usuario.value.endereco.logradouro = data.endereco?.logradouro || ''
    usuario.value.endereco.numero = data.endereco?.numero || ''
    usuario.value.endereco.complemento = data.endereco?.complemento || ''
    usuario.value.endereco.bairro = data.endereco?.bairro || ''
    usuario.value.endereco.localidade = data.endereco?.localidade || ''
    usuario.value.endereco.uf = data.endereco?.uf || ''
  } catch (err) {
    if (err?.response?.status === 401) {
      message.value = {
        text: 'Sessão expirada. Faça login novamente.',
        type: 'error'
      }
    } else {
      message.value = {
        text: 'Erro ao carregar seus dados. Tente novamente.',
        type: 'error'
      }
    }
  } finally {
    loading.value = false
  }
}

const formatarTelefoneExibicao = (numero) => {
  const digitos = normalizeTelefone(numero)
  if (digitos.length === 11) {
    return `(${digitos.substring(0, 2)}) ${digitos.substring(
      2,
      7
    )}-${digitos.substring(7, 11)}`
  }
  if (digitos.length === 10) {
    return `(${digitos.substring(0, 2)}) ${digitos.substring(
      2,
      6
    )}-${digitos.substring(6, 10)}`
  }
  return numero || ''
}

onMounted(fetchUser)
</script>

<style scoped>
.btn-primary {
  @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30 disabled:opacity-60 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition-all duration-300 ease-in-out;
}

.btn-ghost {
  @apply p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-150 ease-in-out;
}

.card {
  @apply bg-white dark:bg-brand-800 rounded-2xl shadow-lg border border-slate-100 dark:border-brand-700/50 transition-all duration-300 ease-in-out;
}

.form-section-border {
  @apply border-slate-100 dark:border-brand-700/50;
}

.faq-header-border {
  @apply border-b border-slate-100 dark:border-brand-700/50;
}

.faq-divide-border {
  @apply divide-y divide-slate-100 dark:border-brand-700/50;
}

.faq-row {
  @apply p-6 flex items-center justify-between transition-colors duration-300 ease-in-out hover:bg-slate-50 dark:hover:bg-brand-700/30;
}

.faq-q {
  @apply text-sm font-medium text-slate-800 dark:text-slate-100;
}

.faq-a {
  @apply text-xs text-slate-600 dark:text-slate-400 mt-1;
}

.user-avatar {
  @apply flex items-center justify-center w-24 h-24 rounded-full shadow-lg ring-4 ring-brand-500/20 dark:ring-brand-600/50 transition-all duration-300 ease-in-out;
}

.user-avatar:hover {
  @apply scale-105;
}

.stat-row {
  @apply bg-slate-50/70 dark:bg-brand-900/40 rounded-lg px-4 py-3 border border-slate-100 dark:border-brand-700/50 transition-all duration-300 ease-in-out;
}

.stat-row:hover {
  @apply shadow-sm bg-slate-100 dark:bg-brand-700/60;
}

.stat-row dt {
  @apply text-xs font-medium text-slate-500 dark:text-slate-400 mb-0.5;
}

.stat-row dd {
  @apply text-sm font-semibold text-slate-800 dark:text-slate-100;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>